import { CSRF_HEADER, errorEnvelopeSchema, type ErrorCode } from '@gc/shared';

/** Error de API con el código del contrato, para que la UI decida sin leer textos. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = '/api/v1';

/**
 * El token CSRF se guarda en memoria, no en localStorage: no debe sobrevivir a
 * la pestaña ni quedar accesible a otro script.
 */
let csrfToken: string | undefined;
export function setCsrfToken(token: string | undefined): void {
  csrfToken = token;
}

/** Lo necesita la subida por XMLHttpRequest, que no pasa por `apiRequest`. */
export function getCsrfToken(): string | undefined {
  return csrfToken;
}

type RequestOptions = {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * Devuelve la envoltura completa en lugar de solo `data`. Lo necesitan los
   * endpoints que acompañan la respuesta con `meta` (cursor de paginación,
   * revisión resultante).
   */
  readonly raw?: boolean;
};

/**
 * Cliente same-origin: la sesión viaja en una cookie HttpOnly, así que el
 * navegador la adjunta solo y el JavaScript nunca la ve.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers[CSRF_HEADER] = csrfToken;

  const init: RequestInit = {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  const response = await fetch(`${BASE}${path}`, init);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const parsed = errorEnvelopeSchema.safeParse(payload);
    if (parsed.success) {
      const { code, message, requestId } = parsed.data.error;
      throw new ApiError(code, message, response.status, requestId);
    }
    throw new ApiError('INTERNAL_ERROR', 'Error inesperado del servidor.', response.status);
  }

  if (options.raw === true) return payload as T;
  return (payload as { data: T }).data;
}
